import React, { useState } from 'react';
import { X, ArrowRight, Home, AlertTriangle } from 'lucide-react';
import { Room } from '../types';

interface MoveResidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  rooms: Room[];
  onMoveResident: (
    fromRoomNumber: string,
    fromPosition: 1 | 2,
    toRoomNumber: string,
    toPosition: 1 | 2
  ) => Promise<boolean>;
}

export function MoveResidentModal({ isOpen, onClose, rooms, onMoveResident }: MoveResidentModalProps) {
  const [fromBuildingType, setFromBuildingType] = useState<'gajah4' | 'gajah3' | 'anduring' | 'limau_manis'>('gajah4');
  const [toBuildingType, setToBuildingType] = useState<'gajah4' | 'gajah3' | 'anduring' | 'limau_manis'>('gajah4');
  const [fromRoom, setFromRoom] = useState('');
  const [fromPosition, setFromPosition] = useState<1 | 2 | null>(null);
  const [toRoom, setToRoom] = useState('');
  const [toPosition, setToPosition] = useState<1 | 2 | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Filter rooms by building type
  const getFilteredRooms = (buildingType: 'gajah4' | 'gajah3' | 'anduring' | 'limau_manis') => {
    return rooms.filter(room => {
      if (buildingType === 'gajah4') return room.buildingType === 'gajah4' || !room.buildingType;
      return room.buildingType === buildingType;
    }).sort((a, b) => {
      const numA = parseInt(a.number.replace(/[^\d]/g, ''));
      const numB = parseInt(b.number.replace(/[^\d]/g, ''));
      return numA - numB;
    });
  };

  const fromRooms = getFilteredRooms(fromBuildingType);
  const toRooms = getFilteredRooms(toBuildingType);

  const fromRoomData = rooms.find(r => r.number === fromRoom);
  const toRoomData = rooms.find(r => r.number === toRoom);
  const fromResident = fromRoomData?.residents.find(r => r.position === fromPosition);
  const toResident = toRoomData?.residents.find(r => r.position === toPosition);

  const resetForm = () => {
    setFromBuildingType('gajah4');
    setToBuildingType('gajah4');
    setFromRoom('');
    setFromPosition(null);
    setToRoom('');
    setToPosition(null);
    setValidationError('');
  };

  const validateMove = () => {
    if (!fromRoom || !fromPosition || !toRoom || !toPosition) {
      setValidationError('Semua field harus diisi!');
      return false;
    }

    if (fromRoom === toRoom && fromPosition === toPosition) {
      setValidationError('Tidak bisa memindahkan ke posisi yang sama!');
      return false;
    }

    if (!fromResident || !fromResident.name || !fromResident.name.trim()) {
      setValidationError('Penghuni asal tidak ditemukan atau kosong!');
      return false;
    }

    if (toResident && toResident.name && toResident.name.trim()) {
      setValidationError('Posisi tujuan sudah terisi oleh penghuni lain!');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setValidationError('');
    
    if (!validateMove()) return;

    const confirmed = confirm(
      `Apakah Anda yakin ingin memindahkan ${fromResident?.name} dari Kamar ${fromRoom} (Penghuni ${fromPosition}) ke Kamar ${toRoom} (Penghuni ${toPosition})?\n\n` +
      `Semua data penghuni dan riwayat pembayaran akan dipindahkan ke posisi baru.`
    );

    if (!confirmed) return;

    setIsMoving(true);
    try {
      const success = await onMoveResident(
        fromRoom,
        fromPosition as 1 | 2,
        toRoom,
        toPosition as 1 | 2
      );

      if (success) {
        resetForm();
        onClose();
      }
    } catch (error) {
      console.error('Error moving resident:', error);
      setValidationError('Terjadi kesalahan saat memindahkan penghuni. Silakan coba lagi.');
    } finally {
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Pindah Penghuni
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* FROM Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Home className="h-5 w-5 text-blue-600" />
                Pindah Dari
              </h3>

              {/* From Building Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pilih Kos Asal
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    type="button"
                    onClick={() => {
                      setFromBuildingType('gajah4');
                      setFromRoom('');
                      setFromPosition(null);
                    }} 
                    className={`p-2 rounded border text-sm ${fromBuildingType === 'gajah4' ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                  >
                    Kos Gajah 4
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setFromBuildingType('gajah3');
                      setFromRoom('');
                      setFromPosition(null);
                    }} 
                    className={`p-2 rounded border text-sm ${fromBuildingType === 'gajah3' ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                  >
                    Kos Gajah 3
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setFromBuildingType('anduring');
                      setFromRoom('');
                      setFromPosition(null);
                    }} 
                    className={`p-2 rounded border text-sm ${fromBuildingType === 'anduring' ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                  >
                    Kos Anduring
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setFromBuildingType('limau_manis');
                      setFromRoom('');
                      setFromPosition(null);
                    }} 
                    className={`p-2 rounded border text-sm ${fromBuildingType === 'limau_manis' ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                  >
                    Kos Limau Manis
                  </button>
                </div>
              </div>

              {/* From Room Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kamar Asal
                </label>
                <select
                  value={fromRoom}
                  onChange={(e) => {
                    setFromRoom(e.target.value);
                    setFromPosition(null);
                  }}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">-- Pilih Kamar Asal --</option>
                  {fromRooms
                    .filter(room => room.residents.some(r => r.name && r.name.trim()))
                    .map(room => (
                      <option key={room.number} value={room.number}>
                        Kamar {room.number} 
                        ({room.residents.filter(r => r.name && r.name.trim()).length} penghuni)
                      </option>
                    ))
                  }
                </select>
              </div>

              {/* From Position Selection */}
              {fromRoom && fromRoomData && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Penghuni yang Dipindah
                  </label>
                  <select
                    value={fromPosition || ''}
                    onChange={(e) => setFromPosition(e.target.value ? parseInt(e.target.value) as 1 | 2 : null)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">-- Pilih Penghuni --</option>
                    {[1, 2].map(position => {
                      const resident = fromRoomData.residents.find(r => r.position === position);
                      if (!resident?.name || !resident.name.trim()) return null;
                      return (
                        <option key={position} value={position}>
                          Penghuni {position}: {resident.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* From Resident Info */}
              {fromResident && (
                <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Info Penghuni yang Dipindah
                  </h4>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Nama:</span> {fromResident.name}</p>
                    <p><span className="font-medium">Program Studi:</span> {fromResident.study || '-'}</p>
                    <p><span className="font-medium">No. HP:</span> {fromResident.phone || '-'}</p>
                    <p><span className="font-medium">Pola Pembayaran:</span> {fromResident.paymentPattern} bulan</p>
                    <p><span className="font-medium">Periode Pembayaran:</span> {fromResident.paymentPeriods?.length || 0}</p>
                  </div>
                </div>
              )}
            </div>

            {/* TO Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <ArrowRight className="h-5 w-5 text-green-600" />
                Pindah Ke
              </h3>

              {/* To Building Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pilih Kos Tujuan
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    type="button"
                    onClick={() => {
                      setToBuildingType('gajah4');
                      setToRoom('');
                      setToPosition(null);
                    }} 
                    className={`p-2 rounded border text-sm ${toBuildingType === 'gajah4' ? 'bg-green-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                  >
                    Kos Gajah 4
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setToBuildingType('gajah3');
                      setToRoom('');
                      setToPosition(null);
                    }} 
                    className={`p-2 rounded border text-sm ${toBuildingType === 'gajah3' ? 'bg-green-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                  >
                    Kos Gajah 3
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setToBuildingType('anduring');
                      setToRoom('');
                      setToPosition(null);
                    }} 
                    className={`p-2 rounded border text-sm ${toBuildingType === 'anduring' ? 'bg-green-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                  >
                    Kos Anduring
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setToBuildingType('limau_manis');
                      setToRoom('');
                      setToPosition(null);
                    }} 
                    className={`p-2 rounded border text-sm ${toBuildingType === 'limau_manis' ? 'bg-green-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                  >
                    Kos Limau Manis
                  </button>
                </div>
              </div>

              {/* To Room Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kamar Tujuan
                </label>
                <select
                  value={toRoom}
                  onChange={(e) => {
                    setToRoom(e.target.value);
                    setToPosition(null);
                  }}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">-- Pilih Kamar Tujuan --</option>
                  {toRooms.map(room => (
                    <option key={room.number} value={room.number}>
                      Kamar {room.number} 
                      ({room.residents.filter(r => r.name && r.name.trim()).length}/2 penghuni)
                    </option>
                  ))}
                </select>
              </div>

              {/* To Position Selection */}
              {toRoom && toRoomData && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Posisi Tujuan
                  </label>
                  <select
                    value={toPosition || ''}
                    onChange={(e) => setToPosition(e.target.value ? parseInt(e.target.value) as 1 | 2 : null)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">-- Pilih Posisi --</option>
                    {[1, 2].map(position => {
                      const resident = toRoomData.residents.find(r => r.position === position);
                      const isOccupied = Boolean(resident?.name && resident.name.trim());
                      return (
                        <option 
                          key={position} 
                          value={position}
                          disabled={isOccupied}
                        >
                          Penghuni {position}: {isOccupied && resident ? `${resident.name} (terisi)` : '(kosong)'}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* To Position Info */}
              {toRoom && toPosition && (
                <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-700">
                  <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">
                    Posisi Tujuan
                  </h4>
                  <div className="text-sm">
                    <p><span className="font-medium">Kamar:</span> {toRoom}</p>
                    <p><span className="font-medium">Posisi:</span> Penghuni {toPosition}</p>
                    <p><span className="font-medium">Status:</span> 
                      {toResident?.name && toResident.name.trim() ? 
                        <span className="text-red-600 dark:text-red-400"> Terisi ({toResident.name})</span> : 
                        <span className="text-green-600 dark:text-green-400"> Kosong</span>
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Validation Error Message */}
          {validationError && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  {validationError}
                </p>
              </div>
            </div>
          )}

          {/* Move Summary */}
          {fromResident && toRoom && toPosition && !validationError && (
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                Ringkasan Perpindahan
              </h4>
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                <p className="mb-2">
                  <span className="font-medium">{fromResident.name}</span> akan dipindahkan dari 
                  <span className="font-medium"> Kamar {fromRoom} (Penghuni {fromPosition})</span> ke 
                  <span className="font-medium"> Kamar {toRoom} (Penghuni {toPosition})</span>
                </p>
                <p className="text-xs">
                  • Semua data penghuni akan dipindahkan<br/>
                  • Seluruh riwayat pembayaran ({fromResident.paymentPeriods?.length || 0} periode) akan ikut dipindahkan<br/>
                  • Nomor kamar pada data pembayaran akan diperbarui
                </p>
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t dark:border-gray-700">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isMoving || !fromRoom || !fromPosition || !toRoom || !toPosition}
              className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isMoving ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Memindahkan...
                </div>
              ) : (
                'Pindah Sekarang'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}